import {
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  ListItem,
  ListItemIcon,
  MenuItem,
  Tooltip,
  Typography,
} from "@material-ui/core";
import Grid from "@material-ui/core/Grid";
import Paper from "@material-ui/core/Paper";
import TextField from "@material-ui/core/TextField";
import BusinessIcon from "@material-ui/icons/Business";
import ChatIcon from "@material-ui/icons/Chat";
import InfoIcon from "@material-ui/icons/Info";
import NotificationsIcon from "@material-ui/icons/Notifications";
import React, { useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import SortableTable from "../../Components/SortableTable";
import Title from "../../Components/Title";

import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import {
  ECABIN,
  formatTimeWithTZOffset,
  getInvoiceIssuerData,
  getInvoiceIssuerOptions,
  getInvoiceIssuers,
  getToken,
  getUser,
  invoiceIssuerMatchesValue,
  paymentStates,
  requestErrorHandler,
  searchMatchingList,
} from "../../Utils/Common";
import EnvSettings from "../../Utils/EnvSettings";
import { useStyles } from "../../Utils/Styles";
import EditOrder from "./EditOrder";
import NewOrder from "./NewOrder";

const server = EnvSettings.server;
const DEFAULT_RESUPPLY_FILTER_STATES = [
  "invoiced",
  "deposit",
  "completed",
  "transit",
];

const getInitialFilterStates = (orderId) =>
  orderId ? paymentStates.map((m) => m.value) : DEFAULT_RESUPPLY_FILTER_STATES;

const mergeOrdersById = (currentOrders, newOrders) => {
  const ordersById = new Map();
  [...(currentOrders || []), ...(newOrders || [])].forEach((order) => {
    if (order?.id) {
      ordersById.set(order.id, order);
    }
  });
  return Array.from(ordersById.values());
};

export default function ResupplyOrders(props) {
  const order_id = props?.computedMatch?.params?.order_id;
  const classes = useStyles();
  const [searchList, setSearchList] = useState();
  const [orderList, setOrderList] = useState();
  const [searchValue, setSearchValue] = useState(() => order_id || "");
  const [filterStates, setFilterStates] = useState(() =>
    getInitialFilterStates(order_id),
  );
  const [fetchedFilterStates, setFetchedFilterStates] = useState([]);
  const [loadingFilterStates, setLoadingFilterStates] = useState([]);
  const [selectedOrder, setSelectedOrder] = useState();
  const [loading, setLoading] = useState(false);
  const { t } = useTranslation();
  const [buyerCompany, setBuyerCompany] = useState();
  const [invoiceIssuers, setInvoiceIssuers] = useState([]);
  const dispatch = useDispatch();
  const history = useHistory();
  useEffect(() => {
    getInvoiceIssuers(setInvoiceIssuers, dispatch);
  }, [dispatch]);

  const editBoxRef = useRef(null);

  const executeScroll = () => {
    editBoxRef.current.scrollIntoView({ behavior: "smooth" });
  };

  const showOrder = (dev) => {
    setSelectedOrder();
    const requestOptions = {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        token: getToken(),
        view: "detail",
        order_id: dev.id,
      }),
    };
    fetch(server + "/get_resupply_orders", requestOptions)
      .then((res) => res.json())
      .then((result) => {
        requestErrorHandler(result, dispatch);
        if (!result.error) {
          // Hack for refreshing the selected device component
          setTimeout(() => {
            setSelectedOrder(result.orders?.[0] || dev);
            if (editBoxRef && editBoxRef.current) {
              executeScroll();
            }
          }, 1);
        }
      })
      .catch(() => {
        setTimeout(() => {
          setSelectedOrder(dev);
          if (editBoxRef && editBoxRef.current) {
            executeScroll();
          }
        }, 1);
      });
  };

  const getResupplyOrders = (statesToFetch, replace) => {
    const paymentStatesToFetch = Array.isArray(statesToFetch)
      ? statesToFetch.filter(Boolean)
      : filterStates;
    if (!paymentStatesToFetch.length) {
      if (replace) {
        setOrderList([]);
        setSearchList([]);
        setFetchedFilterStates([]);
      }
      return;
    }
    setLoading(true);
    setLoadingFilterStates((currentStates) => [
      ...new Set([...currentStates, ...paymentStatesToFetch]),
    ]);
    if (replace) {
      setSearchList();
      setSelectedOrder();
    }
    const requestOptions = {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        token: getToken(),
        view: "list",
        paymentStates: paymentStatesToFetch,
      }),
    };
    fetch(server + "/get_resupply_orders", requestOptions)
      .then((res) => res.json())
      .then((result) => {
        requestErrorHandler(result, dispatch);
        if (!result.error) {
          const fetchedOrders = result.orders || [];
          setOrderList((currentOrders) =>
            replace
              ? fetchedOrders
              : mergeOrdersById(currentOrders, fetchedOrders),
          );
          setFetchedFilterStates((currentStates) =>
            replace
              ? paymentStatesToFetch
              : [...new Set([...currentStates, ...paymentStatesToFetch])],
          );
        }
        setLoading(false);
        setLoadingFilterStates((currentStates) =>
          currentStates.filter(
            (state) => !paymentStatesToFetch.includes(state),
          ),
        );
      })
      .catch((e) => {
        setLoading(false);
        setLoadingFilterStates((currentStates) =>
          currentStates.filter(
            (state) => !paymentStatesToFetch.includes(state),
          ),
        );
      });
  };

  const handleFilterStateChange = (state, selected) => {
    const nextStates = selected
      ? [...new Set([...filterStates, state])]
      : filterStates.filter((filterState) => filterState !== state);
    setFilterStates(nextStates);
    if (selected && !fetchedFilterStates.includes(state)) {
      getResupplyOrders([state], false);
    }
  };

  const filterResults = () => {
    setSearchList(
      orderList &&
        orderList.filter((contact) => {
          return (
            filterStates.find((o) => o === contact.paymentStatus) &&
            searchMatchingList(
              [
                contact.number,
                contact.genericDevices,
                contact.User,
                contact.invoiceIssuer,
                contact.SupplierCompany,
              ],
              searchValue,
            ) &&
            invoiceIssuerMatchesValue(contact.buyer, buyerCompany)
          );
        }),
    );
  };

  useEffect(filterResults, [
    searchValue,
    orderList,
    filterStates,
    buyerCompany,
    invoiceIssuers,
  ]);

  useEffect(() => {
    getResupplyOrders(getInitialFilterStates(order_id), true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <Paper className={classes.paper}>
          <Grid container spacing={1} alignItems="center">
            <Grid item>
              <NewOrder />
            </Grid>
            <Grid item>
              <Button
                variant="outlined"
                startIcon={<BusinessIcon />}
                onClick={() => {
                  history.push("/cpanel/resupply/suppliers");
                }}
              >
                {t("resupplyOrders.supplierCompanies")}
              </Button>
            </Grid>
          </Grid>
        </Paper>
      </Grid>
      <Grid item xs={12}>
        <Paper className={classes.paper} style={{ overflow: "hidden" }}>
          <Grid container spacing={3} alignItems="center">
            <Grid item container spacing={3} xs={12}>
              <Grid item xs={12}>
                <Title>{t("resupplyOrders.header")}</Title>
              </Grid>
            </Grid>
            <Grid item xs={12} sm={3}>
              <TextField
                autoFocus
                label={t("search")}
                type="text"
                fullWidth
                variant="outlined"
                value={searchValue}
                onChange={(e) => {
                  setSearchValue(e.target.value.toLowerCase());
                }}
                ref={editBoxRef}
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <TextField
                label={t("resupplyOrders.buyer")}
                variant="outlined"
                select
                fullWidth
                onChange={(e) => {
                  setBuyerCompany(e.target.value);
                }}
              >
                {[
                  { name: "", data: { name: t("select") } },
                  ...getInvoiceIssuerOptions(invoiceIssuers),
                ].map((m, i) => (
                  <MenuItem key={(m.name || "select") + i} value={m.name}>
                    {m?.data?.name || t("select")}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
          </Grid>
        </Paper>
      </Grid>

      <Grid item xs={12}>
        <Paper className={classes.paper}>
          <EditOrder selectedOrder={selectedOrder} />
          {searchList ? (
            <>
              <Grid container item xs={12}>
                <Grid item xs={11}>
                  <Typography>{t("orders.paymentStatus")}</Typography>
                  {paymentStates
                    .filter((f) => f.value !== "waiting")
                    .map((o) => {
                      const isStateLoading = loadingFilterStates.includes(
                        o.value,
                      );
                        return (
                          <FormControlLabel
                            key={o.label}
                            control={
                              <Checkbox
                                color={o.color || "primary"}
                                disabled={isStateLoading}
                                style={{ color: o.color || "" }}
                                onChange={(e, v) => {
                                  handleFilterStateChange(o.value, v);
                                }}
                                checked={
                                  !!filterStates.find((f) => f === o.value)
                                }
                              />
                            }
                            label={
                              <>
                                {t("paymentStates." + o.value)}
                                {isStateLoading ? (
                                  <CircularProgress
                                    size={12}
                                    style={{ marginLeft: 6 }}
                                  />
                                ) : null}
                              </>
                            }
                          />
                      );
                    })}
                </Grid>
                <Grid item xs={1}></Grid>
              </Grid>
              <SortableTable
                tableLayout={"fixed"}
                downloadButton={true}
                header={t("resupplyOrders.header")}
                headCells={[
                  {
                    id: "Notes",
                    label: "",
                    width: 32,
                    hideFromCsv: true,
                  },
                  {
                    id: "EmailListResupplyOrders",
                    label: "",
                    width: 32,
                    hideFromCsv: true,
                  },
                  {
                    id: "updatedAt",
                    numeric: false,
                    disablePadding: false,
                    label: t("updatedAt"),
                  },
                  {
                    id: "createdAt",
                    numeric: false,
                    disablePadding: false,
                    label: t("createdAt"),
                  },
                  {
                    id: "User.fullName",
                    numeric: false,
                    disablePadding: false,
                    label: t("createdBy"),
                  },
                  {
                    id: "buyer",
                    numeric: false,
                    disablePadding: false,
                    label: t("resupplyOrders.buyer"),
                  },
                  {
                    id: "invoiceIssuer",
                    numeric: false,
                    disablePadding: false,
                    label: t("orders.invoiceIssuer"),
                  },
                  {
                    id: "number",
                    numeric: false,
                    disablePadding: false,
                    label: t("orders.orderNumber"),
                  },
                  {
                    id: "paymentStatus",
                    numeric: false,
                    disablePadding: false,
                    label: t("orders.paymentStatus"),
                  },
                  {
                    id: "genericDevices",
                    numeric: false,
                    disablePadding: false,
                    label: t("items"),
                  },
                  {
                    id: "totalPrice",
                    numeric: false,
                    disablePadding: false,
                    label: t("orders.totalPrice"),
                  },
                ]}
                // disablePagination: true,
                defaultSortRow={"updatedAt"}
                handleRowClick={showOrder}
                getRowStyle={(data) => {
                  var obj = { cursor: "pointer" };
                  var p = paymentStates.find(
                    (f) => data.paymentStatus === f.value,
                  );
                  if (p && p.color) {
                    return { ...obj, backgroundColor: p.color };
                  }
                  return obj;
                }}
                specialDataFormatting={[
                  {
                    id: "updatedAt",
                    format: (data, d, dd) => {
                      if (dd.Notes && dd.Notes.length) {
                        const max = dd.Notes.reduce((prev, current) =>
                          prev && prev.id > current.id ? prev : current,
                        );
                        return formatTimeWithTZOffset(max.updatedAt, ECABIN);
                      }
                      if (dd.activityUpdatedAt) {
                        return formatTimeWithTZOffset(
                          dd.activityUpdatedAt,
                          ECABIN,
                        );
                      }
                      return formatTimeWithTZOffset(data, ECABIN);
                    },
                  },
                  {
                    id: "invoiceIssuer",
                    format: (data, d, dd) => {
                      if (dd.SupplierCompany) {
                        return dd.SupplierCompany.name;
                      }
                      return data;
                    },
                  },
                  {
                    id: "Notes",
                    format: (data) => {
                      if (data?.find((f) => f.User)) {
                        return (
                          <Tooltip title={t("activeComments")}>
                            <ChatIcon
                              color="primary"
                              style={{ fontSize: 20 }}
                            />
                          </Tooltip>
                        );
                      }
                      return "";
                    },
                  },
                  {
                    id: "EmailListResupplyOrders",
                    format: (data, d, dd) => {
                      if (
                        dd.isSubscribed ||
                        (data &&
                          data.find(
                            (f) =>
                              f &&
                              f.EmailList?.EmailListUsers.find(
                                (u) => u.User.id === getUser().userId,
                              ),
                          )) ||
                        dd.UserId === getUser().userId
                      ) {
                        return (
                          <Tooltip title={t("tagged")}>
                            <NotificationsIcon
                              color="primary"
                              style={{ fontSize: 20 }}
                            />
                          </Tooltip>
                        );
                      }
                      return "";
                    },
                  },
                  {
                    id: "createdAt",
                    format: (data) => {
                      return formatTimeWithTZOffset(data, ECABIN);
                    },
                  },
                  {
                    id: "genericDevices",
                    format: (data) => {
                      if (data && data.length > 1) {
                        var x = 0;
                        data.forEach((d) => (x = x + d.count));
                        return t("count") + ":" + x;
                      }
                      var a = "";
                      data.forEach((v) => (a = v.count + " x " + v.name));
                      return a;
                    },
                  },
                  {
                    id: "paymentStatus",
                    formatReturnsElement: true,
                    format: (data, e, v) => {
                      if (e === "json") {
                        return t(
                          "paymentStates." +
                            paymentStates.find((o) => o.value === data).value,
                        );
                      }
                      return (
                        <ListItem>
                          {t(
                            "paymentStates." +
                              paymentStates.find((o) => o.value === data).value,
                          )}
                          {data === "transit" && v.multipleShipments ? (
                            <Tooltip
                              title={t("orders.multipleShipments")}
                              placement="top"
                            >
                              <ListItemIcon size="small">
                                <InfoIcon
                                  style={{ verticalAlign: "middle" }}
                                  fontSize="small"
                                />
                              </ListItemIcon>
                            </Tooltip>
                          ) : null}
                        </ListItem>
                      );
                    },
                  },
                  {
                    id: "buyer",
                    format: (data) => {
                      if (!data) {
                        return data;
                      }
                      return (
                        getInvoiceIssuerData(invoiceIssuers, data)?.name || data
                      );
                    },
                  },
                ]}
                // handleScrollClick: showDeviceNewTab,
                tableData={searchList}
                rowsPerPage={50}
              />
            </>
          ) : loading ? (
            <CircularProgress />
          ) : undefined}
        </Paper>
      </Grid>
    </Grid>
  );
}
